import React, { useState, useRef, useEffect } from 'react';
import { useConfig } from '../../config/ConfigContext';
import './VideoPlayer.css';

/**
 * Video Player Component with mini-player and full-screen support
 * Based on the existing shorts player but adapted for horizontal videos
 */
const VideoPlayer = ({ video, onClose, autoplay = true, onPlay, onPause, onEnded }) => {
  const { config, theme } = useConfig();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showReplayOverlay, setShowReplayOverlay] = useState(false);

  // Clip bounds from video prop (in seconds)
  const clipStartTime = video?.clipStartTime || 0;
  const clipEndTime = video?.clipEndTime || null; // null means play to end

  // Calculate clip duration (effective duration for the user)
  const [fullVideoDuration, setFullVideoDuration] = useState(0);
  const clipDuration = clipEndTime !== null
    ? clipEndTime - clipStartTime
    : (fullVideoDuration > 0 ? fullVideoDuration - clipStartTime : 0);
  
  // Device detection
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;
  
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const hideTimeout = useRef(null);
  const youtubePlayerRef = useRef(null);

  const themeColor = config?.primaryColor || theme?.colors?.primary || '#1e4d8b';

  // Initialize YouTube player
  useEffect(() => {
    if (!video?.youtube_video_id) return;

    // Load YouTube API if not already loaded
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // Initialize player when API is ready
    let retryCount = 0;
    const MAX_RETRIES = 10;
    let retryTimer = null;

    const initializePlayer = () => {
      // Ensure the DOM element is ready
      if (!playerRef.current) {
        retryCount++;
        if (retryCount > MAX_RETRIES) {
          console.warn('VideoPlayer: playerRef not ready after max retries, aborting.');
          return;
        }
        retryTimer = setTimeout(initializePlayer, 100);
        return;
      }

      if (youtubePlayerRef.current) {
        youtubePlayerRef.current.destroy();
      }

      // Build playerVars with optional clip bounds
      const playerVarsConfig = {
        autoplay: autoplay ? 1 : 0,
        mute: 0,
        controls: 0,
        disablekb: 1,
        fs: 0, // Disable YouTube fullscreen button for all devices
        modestbranding: 1,
        playsinline: 1,
        rel: 0, // Limit related videos to same channel
        origin: window.location.origin,
        enablejsapi: 1,
        iv_load_policy: 3, // Disable video annotations
        showinfo: 0 // Hide video info (deprecated but might still work)
      };

      // Add clip start time if specified
      if (clipStartTime > 0) {
        playerVarsConfig.start = Math.floor(clipStartTime);
      }

      // Add clip end time if specified (not "end" which means null)
      if (clipEndTime !== null) {
        playerVarsConfig.end = Math.floor(clipEndTime);
      }

      youtubePlayerRef.current = new window.YT.Player(playerRef.current, {
        videoId: video.youtube_video_id,
        playerVars: playerVarsConfig,
        events: {
          onReady: onPlayerReady,
          onStateChange: onPlayerStateChange,
          onError: onPlayerError
        }
      });
    };

    if (window.YT && window.YT.Player) {
      initializePlayer();
    } else {
      window.onYouTubeIframeAPIReady = initializePlayer;
    }

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      if (youtubePlayerRef.current) {
        youtubePlayerRef.current.destroy();
      }
    };
  }, [video?.youtube_video_id, clipStartTime, clipEndTime]);

  const onPlayerReady = (event) => {
    const player = event.target;
    const totalDuration = player.getDuration();

    // Store full video duration for clip calculations
    setFullVideoDuration(totalDuration);

    // Set the effective duration (clip duration or remaining duration after start)
    const effectiveEnd = clipEndTime !== null ? clipEndTime : totalDuration;
    const effectiveDuration = effectiveEnd - clipStartTime;
    setDuration(effectiveDuration > 0 ? effectiveDuration : totalDuration);

    setVolume(player.getVolume() / 100);
    setIsMuted(player.isMuted());

    // Only autoplay if explicitly enabled
    if (autoplay) {
      try {
        player.playVideo();
      } catch (error) {
        console.log('Autoplay may be blocked by browser:', error);
      }
    }
  };

  const onPlayerStateChange = (event) => {
    const player = event.target;
    const state = event.data;
    
    if (state === window.YT.PlayerState.PLAYING) {
      setIsPlaying(true);
      setShowReplayOverlay(false);
      startTimeUpdate();
      if (onPlay) onPlay();
    } else if (state === window.YT.PlayerState.PAUSED) {
      setIsPlaying(false);
      stopTimeUpdate();
      if (onPause) onPause();
    } else if (state === window.YT.PlayerState.ENDED) {
      setIsPlaying(false);
      stopTimeUpdate();
      setShowReplayOverlay(true);
      if (onEnded) onEnded();
    }
  };

  const onPlayerError = (event) => {
    console.error('YouTube player error:', event.data);
  };

  const timeUpdateInterval = useRef(null);

  const startTimeUpdate = () => {
    if (timeUpdateInterval.current) return;

    timeUpdateInterval.current = setInterval(() => {
      if (youtubePlayerRef.current) {
        const absoluteTime = youtubePlayerRef.current.getCurrentTime();

        // Convert to relative clip time (time within the clip, not absolute video time)
        const relativeTime = Math.max(0, absoluteTime - clipStartTime);
        setCurrentTime(relativeTime);

        // Enforce clip end time (backup in case YouTube's end param doesn't trigger properly)
        if (clipEndTime !== null && absoluteTime >= clipEndTime) {
          youtubePlayerRef.current.pauseVideo();
          setShowReplayOverlay(true);
          setIsPlaying(false);
          stopTimeUpdate();
          if (onEnded) onEnded();
        }
      }
    }, 500); // Update more frequently for smoother progress bar
  };

  const stopTimeUpdate = () => {
    if (timeUpdateInterval.current) {
      clearInterval(timeUpdateInterval.current);
      timeUpdateInterval.current = null;
    }
  };

  // Auto-hide controls in fullscreen
  useEffect(() => {
    if (isFullScreen && isPlaying) {
      const resetTimeout = () => {
        if (hideTimeout.current) {
          clearTimeout(hideTimeout.current);
        }
        hideTimeout.current = setTimeout(() => {
          setControlsVisible(false);
        }, 3000);
      };

      resetTimeout();
      return () => {
        if (hideTimeout.current) {
          clearTimeout(hideTimeout.current);
        }
      };
    }
  }, [isFullScreen, isPlaying, controlsVisible]);

  const showControls = () => {
    setControlsVisible(true);
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
    }
  };

  const togglePlayPause = () => {
    if (!youtubePlayerRef.current) return;
    
    // Check if the player methods are available
    const player = youtubePlayerRef.current;
    if (typeof player.getPlayerState !== 'function') {
      console.warn('YouTube player not ready yet');
      return;
    }
    
    if (isPlaying) {
      if (typeof player.pauseVideo === 'function') {
        player.pauseVideo();
      }
    } else {
      if (typeof player.playVideo === 'function') {
        player.playVideo();
      }
    }
  };

  const handleSeek = (e) => {
    if (!youtubePlayerRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;

    // Calculate relative time within clip (percentage of clip duration)
    const relativeTime = percentage * duration;

    // Convert to absolute YouTube time (add clip start offset)
    const absoluteTime = clipStartTime + relativeTime;

    // Ensure we stay within clip bounds
    const effectiveEnd = clipEndTime !== null ? clipEndTime : fullVideoDuration;
    const constrainedTime = Math.max(clipStartTime, Math.min(absoluteTime, effectiveEnd));

    youtubePlayerRef.current.seekTo(constrainedTime);
    setCurrentTime(relativeTime); // Display relative time
  };

  const toggleMute = () => {
    if (!youtubePlayerRef.current) return;
    
    if (isMuted) {
      youtubePlayerRef.current.unMute();
      setIsMuted(false);
    } else {
      youtubePlayerRef.current.mute();
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e) => {
    if (!youtubePlayerRef.current) return;
    
    const newVolume = parseFloat(e.target.value);
    youtubePlayerRef.current.setVolume(newVolume * 100);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleFullScreen = () => {
    if (!containerRef.current) return;
    
    if (!isFullScreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
      setIsFullScreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullScreen(false);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div 
      ref={containerRef}
      className={`video-player-container ${isFullScreen ? 'fullscreen' : ''} ${video?.is_live_now ? 'is-live' : ''}`}
      onMouseMove={showControls}
      onTouchStart={showControls}
    >
      {/* Video Player */}
      <div className="video-player-wrapper">
        <div 
          ref={playerRef}
          className="video-player"
        />
        
        {/* Live Indicator */}
        {video?.is_live_now && (
          <div className="video-live-indicator">
            <span className="live-badge">LIVE</span>
          </div>
        )}
        
        {/* Click overlay to intercept center area interactions only */}
        <div 
          className="video-click-overlay"
          onClick={(e) => {
            e.stopPropagation();
            setHasInteracted(true);
            showControls(); // Show controls on click
            togglePlayPause();
          }}
          style={{
            position: 'absolute',
            top: '20%',
            left: 0,
            width: '100%',
            height: '60%', // Only cover the center area, not top/bottom controls
            zIndex: 1,
            cursor: 'pointer'
          }}
        />
        
        {/* Always Visible Timer - Outside controls overlay */}
        {duration > 0 && (
          <div className="always-visible-timer">
            <span className="timer-text">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
        )}
        
        {/* Controls Overlay */}
        <div className={`video-controls ${controlsVisible ? 'visible' : 'hidden'}`}>
          {/* Top Controls */}
          <div className="controls-top">
            {/* Empty for now - can add other top controls if needed */}
          </div>

          {/* Center Play/Pause Button - Only show when video is paused */}
          <div className="controls-center">
            {!isPlaying && (
              <button 
                className="control-button play-pause-button"
                onClick={togglePlayPause}
                aria-label="Play"
              >
                <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </button>
            )}
          </div>

          {/* Bottom Controls */}
          <div className="controls-bottom">
            {/* Progress Bar */}
            <div className="progress-container">
              <div 
                className="progress-bar"
                onClick={handleSeek}
              >
                <div 
                  className="progress-fill"
                  style={{ 
                    width: `${progress}%`,
                    backgroundColor: themeColor
                  }}
                />
              </div>
            </div>

            {/* Control Buttons */}
            <div className="control-buttons">
              <div className="controls-left">
                <button 
                  className="control-button"
                  onClick={togglePlayPause}
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  )}
                </button>

                {/* Volume Control */}
                <div className="volume-control">
                  <button 
                    className="control-button"
                    onClick={toggleMute}
                    aria-label={isMuted ? 'Unmute' : 'Mute'}
                  >
                    {isMuted || volume === 0 ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                      </svg>
                    )}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="volume-slider"
                  />
                </div>
              </div>

              <div className="controls-right">
                <button 
                  className="control-button"
                  onClick={toggleFullScreen}
                  aria-label={isFullScreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  {isFullScreen ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Replay Overlay */}
        {showReplayOverlay && (
          <div className="replay-overlay">
            <button
              className="replay-button"
              onClick={() => {
                if (youtubePlayerRef.current) {
                  // Seek to clip start time (not 0) to replay the clip
                  youtubePlayerRef.current.seekTo(clipStartTime);
                  youtubePlayerRef.current.playVideo();
                  setShowReplayOverlay(false);
                  setCurrentTime(0); // Reset relative time display
                }
              }}
              aria-label="Replay clip"
            >
              <svg width="64" height="64" viewBox="0 0 24 24" fill="white">
                <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
              </svg>
              <span className="replay-text">Replay</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;